import React, { useState, useEffect } from 'react';
import api from '../api/client';
import {
  ACCENT, ACCENT_BG, BORDER_SUBTLE, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_FAINT,
  NEU_BG, NEU_RAISED,
} from '../theme';

const PERMISSION_GROUPS = [
  { group: '📊 מה חדש', items: [
    { key: 'dashboard.access',   label: 'גישה לעמוד' },
    { key: 'dashboard.kpi',      label: 'כרטיסים בסופ״ש' },
    { key: 'dashboard.insights', label: 'מכירה מוקדמת' },
    { key: 'dashboard.top_movies', label: 'טבלאות מובילים (היום/סופ״ש)' },
  ]},
  { group: '📋 טבלאות ודירוגים', items: [
    { key: 'charts.access',   label: 'עמוד טבלאות (יומי/סופ״ש/שבועי)' },
    { key: 'rankings.access', label: 'עמוד דירוגים' },
    { key: 'showdown.access', label: 'עמוד השוואה (ראש בראש)' },
    { key: 'archive.access',  label: 'עמוד סופי שבוע' },
  ]},
  { group: '🔮 תחזיות', items: [
    { key: 'predictions.access', label: 'עמוד תחזיות (ניסיוני — עדיין לא מוכן)' },
  ]},
  { group: '🎪 אירועים', items: [
    { key: 'events.access', label: 'גישה לעמוד אירועים' },
    { key: 'events.manage', label: 'ניהול אירועים (הוספה/עריכה/מחיקה)' },
  ]},
  { group: '📈 אנליטיקה', items: [
    { key: 'analytics.access',              label: 'גישה לעמוד' },
    { key: 'analytics.hourly_chart',        label: 'גרף שעתי' },
    { key: 'analytics.weekly_chart',        label: 'גרף יום בשבוע' },
    { key: 'analytics.trends',             label: 'גרף מגמות סרטים' },
    { key: 'analytics.dead_screenings',    label: 'הקרנות כושלות' },
    { key: 'analytics.branch_efficiency',  label: 'יעילות סניפים' },
    { key: 'analytics.genre_chart',        label: 'גרף ז\'אנרים' },
    { key: 'analytics.blocked_seats_debug', label: 'דיבאג מושבים חסומים' },
  ]},
  { group: '🎬 סרטים', items: [
    { key: 'movies.access',          label: 'גישה לעמוד' },
    { key: 'movies.search_filter',   label: 'חיפוש וסינון' },
    { key: 'movies.occupancy_bar',   label: 'שורת תפוסה' },
    { key: 'movies.approve_blocked', label: 'אישור/חסימת מושבים' },
    { key: 'movies.canonical_link',  label: 'קישור קאנוני' },
    { key: 'movies.tmdb_match',      label: 'שיוך TMDB' },
    { key: 'movies.filter_movie',    label: 'סינון סרט מסטטיסטיקה' },
  ]},
  { group: '🎞️ פרטי סרט', items: [
    { key: 'movie_detail.access',          label: 'גישה לדף' },
    { key: 'movie_detail.stats',           label: 'סטטיסטיקות סיכום' },
    { key: 'movie_detail.screenings_tab',  label: 'טאב "הקרנות"' },
    { key: 'movie_detail.scan_now',        label: 'כפתור "סרוק עכשיו"' },
    { key: 'movie_detail.last_scan',       label: 'עמודת "סריקה אחרונה"' },
    { key: 'movie_detail.manual_update',   label: 'עמודת "עדכון ידני"' },
    { key: 'movie_detail.snapshot_popup',  label: 'באדג\' ופופאפ "נסגר"' },
    { key: 'movie_detail.unscan_popup',    label: 'באדג\' ופופאפ "לא נסרק"' },
    { key: 'movie_detail.charts_tab',      label: 'טאב "גרפים"' },
    { key: 'movie_detail.cinemas_tab',     label: 'טאב "בתי קולנוע"' },
    { key: 'movie_detail.comps_tab',       label: 'טאב "קומפים" (השוואה היסטורית)' },
  ]},
  { group: '🏛️ סניפים', items: [
    { key: 'cinemas.access', label: 'גישה לעמוד (כולל עמוד סניף)' },
    { key: 'cinemas.kpi',    label: 'כרטיסי תקופה' },
    { key: 'cinemas.table',  label: 'דירוג רשתות וסניפים' },
    { key: 'cinemas.cities', label: 'לשונית ערים' },
  ]},
  { group: '⚙️ גריידה', items: [
    { key: 'scrape.access',            label: 'גישה לעמוד' },
    { key: 'scrape.approve_blocked',   label: 'אישור/דחיית מושבים חסומים' },
    { key: 'scrape.lev_seatplan',      label: 'ניהול סיט-פלאן Lev' },
    { key: 'scrape.trigger_full',      label: 'הרצת גריידה מלאה' },
    { key: 'scrape.trigger_tickets',   label: 'הרצת עדכון כרטיסים' },
    { key: 'scrape.schedule_panel',    label: 'לוח זמנים + כרטיסי בריאות ואבחון' },
    { key: 'scrape.logs',              label: 'לוג גריידה' },
  ]},
  { group: '🔧 ניהול', items: [
    { key: 'admin.access',           label: 'גישה לעמוד ניהול' },
    { key: 'admin.users',            label: 'ניהול משתמשים' },
    { key: 'admin.roles',            label: 'ניהול סוגי משתמשים' },
    { key: 'admin.scraper_debug',    label: 'דיבאג Scraper' },
    { key: 'admin.add_movie',        label: 'הוספת סרט ידנית' },
    { key: 'admin.canonical_movies', label: 'מיזוג/קישור קאנוני' },
    { key: 'admin.danger_zone',      label: 'אזור מסוכן — איפוס הקרנות' },
    { key: 'admin.reset_blocked',    label: 'איפוס מושבים חסומים' },
    { key: 'admin.approved_movies',  label: 'רשימת סרטים מאושרים' },
    { key: 'admin.hall_capacities',  label: 'קיבולות אולמות' },
    { key: 'admin.tmdb_settings',    label: 'הגדרות TMDB' },
    { key: 'admin.tmdb_matching',    label: 'התאמת TMDB + לא מותאמים' },
    { key: 'admin.decay_probe',      label: 'Decay Probe' },
  ]},
  { group: '🐛 דיבאג שרשרות', items: [
    { key: 'debug.chain_pages', label: 'דפי דיבאג שרשרות' },
  ]},
];

const ALL_PERM_KEYS = PERMISSION_GROUPS.flatMap(g => g.items.map(i => i.key));
const emptyPerms = () => Object.fromEntries(ALL_PERM_KEYS.map(k => [k, false]));

function RoleForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [perms, setPerms] = useState(initial?.permissions ?? emptyPerms());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (key) => setPerms(p => ({ ...p, [key]: !p[key] }));

  const handleSave = async () => {
    if (!name.trim()) { setErr('יש להזין שם לסוג המשתמש'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ name: name.trim(), permissions: perms });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: NEU_RAISED, border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 12, padding: '20px 18px', marginBottom: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, display: 'block', marginBottom: 5 }}>שם סוג המשתמש</label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="לדוגמה: צופה, מנתח, מפעיל..."
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '9px 12px', borderRadius: 8,
            border: `1px solid ${BORDER_SUBTLE}`,
            background: NEU_BG, color: TEXT_PRIMARY,
            fontSize: 14, fontFamily: 'Heebo, sans-serif', direction: 'rtl',
          }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, marginBottom: 10 }}>הרשאות גישה</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {PERMISSION_GROUPS.map(g => {
            const groupKeys = g.items.map(i => i.key);
            const allOn = groupKeys.every(k => !!perms[k]);
            const toggleGroup = () => {
              const val = !allOn;
              setPerms(p => {
                const next = { ...p };
                groupKeys.forEach(k => { next[k] = val; });
                return next;
              });
            };
            return (
              <div key={g.group} style={{ border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 10, overflow: 'hidden' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: NEU_BG, cursor: 'pointer',
                }} onClick={toggleGroup}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY }}>{g.group}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                    background: allOn ? ACCENT_BG : 'var(--surf-alt)',
                    color: allOn ? ACCENT : TEXT_FAINT,
                  }}>
                    {groupKeys.filter(k => !!perms[k]).length}/{groupKeys.length}
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 12px', background: 'var(--surf-alt)' }}>
                  {g.items.map(p => (
                    <label key={p.key} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
                      border: `1px solid ${perms[p.key] ? ACCENT : BORDER_SUBTLE}`,
                      background: perms[p.key] ? ACCENT_BG : NEU_BG,
                      color: perms[p.key] ? ACCENT : TEXT_SECONDARY,
                      fontSize: 12, fontWeight: perms[p.key] ? 600 : 400,
                      userSelect: 'none', transition: 'all .15s',
                    }}>
                      <input type="checkbox" checked={!!perms[p.key]} onChange={() => toggle(p.key)} style={{ display: 'none' }} />
                      {perms[p.key] ? '✓' : '○'} {p.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {err && <div style={{ color: 'var(--danger, #ef4444)', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '8px 18px', borderRadius: 8, border: 'none',
          background: ACCENT, color: '#fff',
          fontSize: 13, fontWeight: 600, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
        }}>
          {saving ? 'שומר...' : 'שמור'}
        </button>
        <button onClick={onCancel} style={{
          padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDER_SUBTLE}`,
          background: 'none', color: TEXT_SECONDARY,
          fontSize: 13, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
        }}>ביטול</button>
      </div>
    </div>
  );
}

export default function RoleManagementPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/admin/user-roles')
      .then(r => setRoles(r.data))
      .catch(() => setError('שגיאה בטעינת סוגי משתמשים'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (data) => {
    await api.post('/admin/user-roles', data);
    setShowCreate(false);
    load();
  };

  const handleUpdate = async (id, data) => {
    await api.put(`/admin/user-roles/${id}`, data);
    setEditId(null);
    load();
  };

  const handleDelete = async (role) => {
    if (role.user_count > 0) {
      alert(`לא ניתן למחוק — ${role.user_count} משתמשים משויכים לסוג זה`);
      return;
    }
    if (!window.confirm(`למחוק את סוג המשתמש "${role.name}"?`)) return;
    try {
      await api.delete(`/admin/user-roles/${role.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'שגיאה במחיקה');
    }
  };

  if (loading) return <div style={{ color: TEXT_FAINT, padding: 20 }}>טוען...</div>;
  if (error) return <div style={{ color: 'var(--danger, #ef4444)', padding: 20 }}>{error}</div>;

  return (
    <div style={{ direction: 'rtl', fontFamily: 'Heebo, sans-serif', maxWidth: 700 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY }}>סוגי משתמשים</div>
          <div style={{ fontSize: 12, color: TEXT_FAINT, marginTop: 2 }}>הגדר תבניות הרשאות לסוגי משתמשים שונים</div>
        </div>
        {!showCreate && (
          <button onClick={() => setShowCreate(true)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: ACCENT, color: '#fff',
            fontSize: 13, fontWeight: 600, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
          }}>
            + סוג חדש
          </button>
        )}
      </div>

      {showCreate && (
        <RoleForm
          onSave={handleCreate}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {roles.length === 0 && !showCreate && (
        <div style={{ color: TEXT_FAINT, fontSize: 14, padding: '20px 0' }}>אין סוגי משתמשים. צור את הראשון.</div>
      )}

      {roles.map(role => (
        <div key={role.id}>
          {editId === role.id ? (
            <RoleForm
              initial={role}
              onSave={data => handleUpdate(role.id, data)}
              onCancel={() => setEditId(null)}
            />
          ) : (
            <div style={{
              background: NEU_RAISED,
              border: `1px solid ${BORDER_SUBTLE}`,
              borderRadius: 12, padding: '14px 16px',
              marginBottom: 10,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 6 }}>{role.name}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {PERMISSION_GROUPS.map(g => {
                    const granted = g.items.filter(i => role.permissions?.[i.key]).length;
                    if (granted === 0) return null;
                    const groupLabel = g.group.split(' ').slice(1).join(' ');
                    return (
                      <span key={g.group} style={{
                        padding: '2px 9px', borderRadius: 6, fontSize: 11,
                        background: ACCENT_BG, color: ACCENT, fontWeight: 600,
                      }}>{groupLabel} ({granted}/{g.items.length})</span>
                    );
                  })}
                  {ALL_PERM_KEYS.every(k => !role.permissions?.[k]) && (
                    <span style={{ color: TEXT_FAINT, fontSize: 12 }}>ללא הרשאות</span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: TEXT_FAINT, whiteSpace: 'nowrap' }}>
                {role.user_count} משתמשים
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditId(role.id)} style={{
                  padding: '5px 12px', borderRadius: 7, border: `1px solid ${BORDER_SUBTLE}`,
                  background: 'none', color: TEXT_SECONDARY,
                  fontSize: 12, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                }}>עריכה</button>
                <button onClick={() => handleDelete(role)} style={{
                  padding: '5px 12px', borderRadius: 7, border: '1px solid var(--danger, #ef4444)',
                  background: 'none', color: 'var(--danger, #ef4444)',
                  fontSize: 12, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                }}>מחיקה</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
